/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ASSETS } from "../lib/assets";
import {
  Phone, Mail, MapPin, Video, Users, Heart, Sparkles, ArrowRight,
  Check, Menu, X, Instagram, Linkedin, Shield, Feather, Waves, Lock, Eye, EyeOff, Pill, Calendar, CreditCard
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { id: "apropos", label: "À propos" },
  { id: "methode", label: "Méthode PSIQ" },
  { id: "afaka", label: "Méthode Afaka" },
  { id: "accompagnements", label: "Accompagnements" },
  { id: "tarifs", label: "Tarifs" },
  { id: "quiz", label: "Quiz" },
  { id: "rdv", label: "RDV en ligne" },
  { id: "formations", label: "Formations" },
  { id: "galerie", label: "Galerie" },
  { id: "contact", label: "Contact" },
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-cream/80 backdrop-blur-md border-b border-ink-soft" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <a href="#top" className="font-display text-xl text-deep-teal tracking-tight" data-testid="logo-home">
          Gwladys <span className="italic text-coral">Malalaka</span>
        </a>
        <nav className="hidden lg:flex gap-7 text-sm" aria-label="Navigation principale">
          {NAV.map(n => (
            <button key={n.id} onClick={() => scrollTo(n.id)} className="nav-link text-ink/80 hover:text-deep-teal" data-testid={`nav-${n.id}`}>
              {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => scrollTo("contact")} className="hidden lg:inline-flex btn-primary text-sm" data-testid="nav-cta-contact">
          Prendre rendez-vous
        </button>
        <button className="lg:hidden text-deep-teal" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-ivory border-t border-ink-soft px-6 py-4 space-y-3" data-testid="mobile-menu">
          {NAV.map(n => (
            <button key={n.id} onClick={() => { scrollTo(n.id); setOpen(false); }} className="block w-full text-left py-2 text-ink/80" data-testid={`mobile-nav-${n.id}`}>
              {n.label}
            </button>
          ))}
          <button onClick={() => { scrollTo("contact"); setOpen(false); }} className="btn-primary w-full mt-2 text-sm" data-testid="mobile-nav-cta">Prendre rendez-vous</button>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="top" className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 overflow-hidden grain" data-testid="hero">
    <div className="absolute -top-20 -right-20 w-[32rem] h-[32rem] rounded-full bg-sage-soft/50 blur-3xl" aria-hidden="true" />
    <div className="absolute bottom-0 -left-40 w-[28rem] h-[28rem] rounded-full blur-3xl" style={{ background: "rgba(232,167,141,.35)" }} aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7 rise">
        <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-ink-soft bg-ivory/60 text-xs tracking-widest uppercase text-teal" data-testid="hero-badge">
          <Waves size={14} /> Thérapie holistique PSIQ · Limoges &amp; visio
        </p>
        {/* SEO: UN SEUL H1 SUR LA PAGE */}
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-deep-teal tracking-tight rise rise-2" data-testid="hero-title">
          Malalaka — <span className="italic text-coral">libre</span>,<br/> pleinement, <span className="font-serif-accent italic">en soi.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base md:text-lg text-ink/75 rise rise-3" data-testid="hero-subtitle">
          Un voyage dans nos eaux — tel un lotus qui grandit dans la vase pour s'ouvrir à la lumière. Et se libère du poids de ses chaînes.
        </p>
        <p className="mt-5 max-w-xl text-sm md:text-base text-ink/65 leading-relaxed rise rise-3">
          Ma méthode <strong className="text-deep-teal">AFAKA</strong> (« libérer » et « réussir » en malgache) articule <strong>4 approches complémentaires</strong> : la <strong>PSIQ</strong> (psychanalyse cellulaire — libère les mémoires du corps), la <strong>méthode Boclet</strong> (reprendre son cerveau en main : techniques d'apprentissage, mémorisation, concentration, mind-mapping et lecture rapide), la <strong>bio-thérapie</strong> (nutrition, micronutrition, symbolique corps-émotions par un autre thérapeute), et l'<strong>écoute active</strong>. Ensemble, elles vous aident à retrouver votre ancrage, votre clarté mentale, votre énergie vitale.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 rise rise-4">
          <button onClick={() => scrollTo("contact")} className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
            Réserver ma 1ʳᵉ séance offerte <ArrowRight size={18} />
          </button>
          <button onClick={() => scrollTo("methode")} className="btn-ghost" data-testid="hero-cta-secondary">Découvrir la méthode</button>
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-ink/70">
          <span className="inline-flex items-center gap-2"><Shield size={16} className="text-teal" /> Cadre éthique &amp; confidentiel</span>
          <span className="inline-flex items-center gap-2"><Feather size={16} className="text-coral" /> 8 ans de formation PSIQ</span>
          <span className="inline-flex items-center gap-2"><Heart size={16} className="text-sage" /> Approche holistique</span>
        </div>
      </div>
      <div className="lg:col-span-5 relative rise rise-3">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-ivory-2">
          <img src={ASSETS.portrait} alt="Portrait de Gwladys Malalaka, thérapeute PSIQ à Limoges" className="w-full h-full object-cover scale-110 protected-img" loading="eager" onContextMenu={(e) => e.preventDefault()} onDragStart={(e) => e.preventDefault()} />
          <div className="absolute inset-0 bg-gradient-to-t from-deep-teal/20 via-transparent to-transparent" />
          <p className="absolute bottom-5 left-5 right-5 text-cream font-display italic text-lg">« Libérer les loyautés invisibles. »</p>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <section className="py-6 border-y border-ink-soft bg-ivory overflow-hidden" data-testid="marquee" aria-hidden="true">
    <div className="marquee-track font-display text-2xl text-deep-teal/70 italic">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="flex gap-12 items-center">
          <span>Burn-out</span><span className="text-coral">◆</span>
          <span>Maternité</span><span className="text-coral">◆</span>
          <span>Dépression post-partum</span><span className="text-coral">◆</span>
          <span>Nid vide</span><span className="text-coral">◆</span>
          <span>Couvade</span><span className="text-coral">◆</span>
          <span>Poids des générations</span><span className="text-coral">◆</span>
          <span>Culture ultramarine</span><span className="text-coral">◆</span>
          <span>Reconquête de soi</span><span className="text-coral">◆</span>
        </div>
      ))}
    </div>
  </section>
);

const CeQuePSIQ = () => (
  <section className="py-24 lg:py-28 bg-ivory protected-text" data-testid="section-ce-que-psiq" onContextMenu={(e) => e.preventDefault()}>
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-coral">La PSIQ vous aidera à…</p>
      <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">
        Le ressenti ne <span className="italic">ment</span> pas.
      </h2>
      <div className="mt-10 grid md:grid-cols-2 gap-12 text-ink/85 text-base md:text-lg leading-relaxed">
        <div>
          <p className="font-display text-deep-teal text-xl mb-4">La PSIQ vous aidera à :</p>
          <ul className="space-y-4">
            <li className="flex gap-3"><span className="text-coral font-display text-2xl leading-none">·</span><span><strong className="text-deep-teal">vous respecter</strong> et vous faire respecter, à vous connecter à votre corps, à votre âme, ne faire qu'un avec vous-même.</span></li>
            <li className="flex gap-3"><span className="text-coral font-display text-2xl leading-none">·</span><span><strong className="text-deep-teal">ressentir</strong>, vous écouter, vous prioriser, apprendre à être <em>égoïste (qui est salvateur)</em> et le différencier de l'égocentrisme.</span></li>
            <li className="flex gap-3"><span className="text-coral font-display text-2xl leading-none">·</span><span><strong className="text-deep-teal">prendre votre juste place</strong> au sein du couple, auprès de vos enfants, parents, famille, connaissances, collègues, employeurs…</span></li>
            <li className="flex gap-3"><span className="text-coral font-display text-2xl leading-none">·</span><span><strong className="text-deep-teal">vous aimer</strong>, aimer la vie, à vous valider — en dehors des attentes familiales, sociétales, professionnelles, religieuses, culturelles.</span></li>
            <li className="flex gap-3"><span className="text-coral font-display text-2xl leading-none">·</span><span><strong className="text-deep-teal">devenir authentique</strong>, autonome, indépendante, incontrôlable — et <em>incontrôlée par soi et par les autres</em>.</span></li>
          </ul>
        </div>
        <div className="space-y-5 font-display font-light">
          <p className="italic text-deep-teal text-xl border-l-2 border-coral pl-5">« Il n'y a pas d'erreurs ou d'échecs, mais des manières différentes d'apprendre. »</p>
          <p>Mes erreurs sont et ont été les moyens de me remettre en question dans ma relation à l'autre et à moi-même… <em>Et je ne suis pas parfaite !</em></p>
          <p>À travers les mots et les maux, vous vous découvrez un autre langage :</p>
          <p className="text-coral font-semibold not-italic">Le ressenti et le corps ne mentent pas.</p>
          <p>Aujourd'hui, j'apprends à être mamie. Mon tracas, comme dirait <em>Kréol'</em> : c'est de savoir ce que je laisse derrière moi à mes enfants, à mon petit-fils. Mon but : m'alléger de ces bagages lourds et ne garder que les bons et beaux voyages.</p>
          <p>À travers mon histoire et ses péripéties — les naissances, les baby-blues, les déprimes et la tristesse du nid vide, un divorce difficile, le burn-out, des relations toxiques… <strong className="text-coral not-italic">Aujourd'hui, je m'en suis sortie !</strong> De mes blessures et souffrances, j'entends, j'écoute, et je vous accompagne dans la compréhension de vos propres passages « <em>pas-sages</em> ».</p>
        </div>
      </div>

      {/* Citation mise en valeur — encart lotus art nouveau */}
      <div className="mt-16 relative" data-testid="citation-lotus">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-cream via-ivory to-sage-soft/50 border border-ink-soft px-8 py-16 md:px-16 md:py-24 text-center">
          {/* Lotus SVG art nouveau — courbes douces, composition botanique */}
          <svg
            className="absolute inset-0 m-auto w-[560px] h-[560px] md:w-[760px] md:h-[760px] opacity-[0.14] pointer-events-none"
            viewBox="0 0 400 400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <linearGradient id="petalGold" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#C9A66B" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#1A3A3E" stopOpacity="0.7" />
              </linearGradient>
              <linearGradient id="leafTeal" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#2C5F63" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#1A3A3E" stopOpacity="0.9" />
              </linearGradient>
            </defs>

            <g fill="none" strokeLinecap="round" strokeLinejoin="round">
              {/* Feuille ronde arrière-plan gauche */}
              <ellipse cx="80" cy="220" rx="70" ry="58" stroke="url(#leafTeal)" strokeWidth="0.8" opacity="0.5" />
              <path d="M 20 220 Q 80 210, 150 225" stroke="url(#leafTeal)" strokeWidth="0.6" opacity="0.4"/>
              <path d="M 25 235 Q 80 225, 140 240" stroke="url(#leafTeal)" strokeWidth="0.6" opacity="0.4"/>
              <path d="M 80 165 L 80 275" stroke="url(#leafTeal)" strokeWidth="0.5" opacity="0.4"/>

              {/* Feuille ronde arrière-plan droite */}
              <ellipse cx="330" cy="190" rx="62" ry="52" stroke="url(#leafTeal)" strokeWidth="0.8" opacity="0.45" />
              <path d="M 275 190 Q 330 180, 395 195" stroke="url(#leafTeal)" strokeWidth="0.6" opacity="0.4"/>
              <path d="M 330 140 L 330 240" stroke="url(#leafTeal)" strokeWidth="0.5" opacity="0.4"/>

              {/* Tiges élancées */}
              <path d="M 200 380 Q 198 280, 200 200" stroke="url(#petalGold)" strokeWidth="1.1"/>
              <path d="M 130 395 Q 135 320, 150 240" stroke="url(#petalGold)" strokeWidth="0.9"/>
              <path d="M 270 395 Q 265 320, 255 245" stroke="url(#petalGold)" strokeWidth="0.9"/>
              <path d="M 90 395 Q 85 330, 75 280" stroke="url(#petalGold)" strokeWidth="0.8" opacity="0.7"/>
              <path d="M 315 395 Q 320 340, 330 295" stroke="url(#petalGold)" strokeWidth="0.8" opacity="0.7"/>

              {/* LOTUS CENTRAL — grand, ouvert, pétales arrondis art-nouveau */}
              <g stroke="url(#petalGold)" strokeWidth="1.3">
                {/* Pétale central arrière */}
                <path d="M 200 200 C 185 160, 195 120, 200 100 C 205 120, 215 160, 200 200 Z"/>
                {/* Pétales latéraux arrière */}
                <path d="M 200 200 C 168 170, 155 130, 155 108 C 175 128, 195 165, 200 200 Z"/>
                <path d="M 200 200 C 232 170, 245 130, 245 108 C 225 128, 205 165, 200 200 Z"/>
                {/* Pétales moyens */}
                <path d="M 200 205 C 155 190, 125 160, 118 135 C 145 158, 180 185, 200 205 Z"/>
                <path d="M 200 205 C 245 190, 275 160, 282 135 C 255 158, 220 185, 200 205 Z"/>
                {/* Pétales avant (courbés, arrondis) */}
                <path d="M 200 210 C 145 215, 110 205, 92 185 C 130 205, 170 215, 200 210 Z"/>
                <path d="M 200 210 C 255 215, 290 205, 308 185 C 270 205, 230 215, 200 210 Z"/>
                {/* Centre */}
                <ellipse cx="200" cy="205" rx="18" ry="10" />
                <path d="M 190 200 Q 200 195, 210 200" strokeWidth="0.7"/>
                <circle cx="195" cy="200" r="0.8" fill="url(#petalGold)"/>
                <circle cx="200" cy="198" r="0.8" fill="url(#petalGold)"/>
                <circle cx="205" cy="200" r="0.8" fill="url(#petalGold)"/>
              </g>

              {/* LOTUS LATÉRAL GAUCHE — plus petit, vue 3/4 */}
              <g stroke="url(#petalGold)" strokeWidth="1.0" opacity="0.88">
                <path d="M 150 240 C 130 225, 118 200, 115 180 C 130 200, 145 225, 150 240 Z"/>
                <path d="M 150 240 C 140 215, 138 190, 145 170 C 150 195, 155 220, 150 240 Z"/>
                <path d="M 150 240 C 160 215, 162 190, 155 170 C 150 195, 145 220, 150 240 Z"/>
                <path d="M 150 240 C 170 225, 182 200, 185 180 C 170 200, 155 225, 150 240 Z"/>
                <path d="M 150 245 C 125 245, 110 235, 105 222 C 125 235, 145 243, 150 245 Z"/>
                <path d="M 150 245 C 175 245, 190 235, 195 222 C 175 235, 155 243, 150 245 Z"/>
                <ellipse cx="150" cy="242" rx="10" ry="5.5" />
              </g>

              {/* LOTUS LATÉRAL DROIT — plus petit encore */}
              <g stroke="url(#petalGold)" strokeWidth="0.95" opacity="0.82">
                <path d="M 255 250 C 240 240, 232 220, 230 205 C 242 220, 252 240, 255 250 Z"/>
                <path d="M 255 250 C 248 228, 247 208, 252 192 C 256 212, 260 232, 255 250 Z"/>
                <path d="M 255 250 C 262 228, 263 208, 258 192 C 254 212, 250 232, 255 250 Z"/>
                <path d="M 255 250 C 270 240, 278 220, 280 205 C 268 220, 258 240, 255 250 Z"/>
                <path d="M 255 254 C 235 254, 222 246, 218 235 C 235 246, 252 253, 255 254 Z"/>
                <path d="M 255 254 C 275 254, 288 246, 292 235 C 275 246, 258 253, 255 254 Z"/>
                <ellipse cx="255" cy="252" rx="9" ry="5" />
              </g>

              {/* BOUTON DE LOTUS GAUCHE */}
              <g stroke="url(#petalGold)" strokeWidth="1.0" opacity="0.85">
                <path d="M 90 280 C 82 270, 78 250, 82 232 C 88 250, 93 268, 90 280 Z"/>
                <path d="M 90 280 C 98 270, 102 250, 98 232 C 92 250, 87 268, 90 280 Z"/>
                <path d="M 90 280 C 85 265, 84 245, 87 228 C 90 248, 93 268, 90 280 Z"/>
                <ellipse cx="90" cy="278" rx="6" ry="4"/>
              </g>

              {/* BOUTON DE LOTUS DROIT */}
              <g stroke="url(#petalGold)" strokeWidth="1.0" opacity="0.85">
                <path d="M 330 295 C 322 283, 318 262, 322 244 C 328 262, 333 281, 330 295 Z"/>
                <path d="M 330 295 C 338 283, 342 262, 338 244 C 332 262, 327 281, 330 295 Z"/>
                <path d="M 330 295 C 325 278, 324 258, 327 240 C 330 260, 333 280, 330 295 Z"/>
                <ellipse cx="330" cy="293" rx="6" ry="4"/>
              </g>

              {/* Ligne d'eau sous les fleurs — onde douce */}
              <path d="M 40 310 C 90 306, 150 314, 200 310 C 250 306, 310 314, 360 310" stroke="url(#leafTeal)" strokeWidth="0.8" opacity="0.5"/>
              <path d="M 50 322 C 100 319, 160 326, 210 322 C 260 319, 320 326, 370 322" stroke="url(#leafTeal)" strokeWidth="0.6" opacity="0.35"/>

              {/* Volutes art-nouveau délicates */}
              <path d="M 30 120 C 50 100, 65 110, 60 130 C 55 145, 40 140, 35 125" stroke="url(#petalGold)" strokeWidth="0.7" opacity="0.5"/>
              <path d="M 370 120 C 350 100, 335 110, 340 130 C 345 145, 360 140, 365 125" stroke="url(#petalGold)" strokeWidth="0.7" opacity="0.5"/>
            </g>
          </svg>
          <div className="relative max-w-3xl mx-auto">
            <p className="font-display italic text-coral text-xs uppercase tracking-[0.3em]">Ma promesse</p>
            <p className="mt-6 font-display italic text-deep-teal text-2xl md:text-4xl lg:text-5xl leading-[1.25]">
              « Je vous tends la main.<br/>
              À vous de décider de la <span className="text-coral not-italic font-normal">prendre</span> ou pas. »
            </p>
            <p className="mt-6 font-display italic text-deep-teal/80 text-lg md:text-2xl leading-relaxed">
              « Je vous donnerai la canne à pêche et les outils ;<br className="hidden md:block"/>
              mais <span className="text-coral not-italic font-semibold">vous seul·e</span> pouvez créer votre <span className="underline decoration-coral/40 decoration-2 underline-offset-4">richesse intérieure</span>. »
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 text-sm text-ink/60 uppercase tracking-[0.2em]">
              <span className="h-px w-10 bg-coral/50"/>
              <span>Gwladys Malalaka</span>
              <span className="h-px w-10 bg-coral/50"/>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 p-8 md:p-10 rounded-3xl bg-deep-teal text-cream">
        <p className="font-display text-xl md:text-2xl leading-relaxed">
          « Nous ne savons pas ce que nous ne savons pas. En cela, nous ne sommes pas <em>coupables</em>, mais <strong className="text-coral-soft" style={{color:"#E8A78D"}}>responsables</strong>. La PSIQ nous apprend à devenir résilient·e, souverain·e, acteur·actrice de notre vie. »
        </p>
      </div>
    </div>
  </section>
);

const Matrix = () => (
  <section className="py-24 lg:py-28 bg-cream relative overflow-hidden" data-testid="section-matrix">
    <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-coral flex items-center gap-2"><Pill size={14} /> Le choix</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">
          Votre <span className="italic">temps</span> est précieux.
        </h2>
        <div className="mt-6 space-y-4 text-ink/80 text-base md:text-lg leading-relaxed">
          <p>Vous êtes au bon endroit si vous souhaitez vous rencontrer au plus profond de vous-même, d'avoir le courage d'emprunter un chemin tantôt agréable, tantôt sinueux et caillouteux.</p>
          <p>Si vous cherchez du contenu intellectuel, des réponses toutes faites avec des recettes miracles : <strong className="text-coral">vous n'êtes pas au bon endroit.</strong></p>
          <p className="font-display italic text-deep-teal text-xl">Quel sera votre choix ?</p>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="p-6 rounded-2xl border-2 border-red-500/40 bg-red-50/30">
            <p className="font-display text-2xl text-red-700">🔴 Rouge</p>
            <p className="mt-2 text-sm text-ink/75">Voir la réalité telle qu'elle est. Conscientiser. Avancer.</p>
          </div>
          <div className="p-6 rounded-2xl border-2 border-blue-500/40 bg-blue-50/30">
            <p className="font-display text-2xl text-ultramarine">🔵 Bleue</p>
            <p className="mt-2 text-sm text-ink/75">Rester dans le confort connu. Remettre à plus tard.</p>
          </div>
        </div>
      </div>
      <div className="relative aspect-square rounded-[2rem] overflow-hidden bg-ivory">
        <img src={ASSETS.cellule} alt="La cellule — au cœur du vivant (photo Merlin · Pexels)" className="w-full h-full object-cover" />
        <p className="absolute bottom-6 left-6 right-6 font-display italic text-cream text-lg bg-deep-teal/70 backdrop-blur px-4 py-2 rounded-xl">Le choix vous appartient.</p>
      </div>
    </div>
  </section>
);

const APropos = () => (
  <section id="apropos" className="py-24 lg:py-32 bg-cream protected-text" data-testid="section-apropos" onContextMenu={(e) => e.preventDefault()}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden">
          <img src={ASSETS.triptyque} alt="Autobiographie — triptyque par Gwladys Malalaka" className="w-full h-full object-cover protected-img" onContextMenu={(e) => e.preventDefault()} onDragStart={(e) => e.preventDefault()} />
        </div>
        <p className="mt-4 text-xs uppercase tracking-widest text-ink/50 font-display italic">Autobiographie — triptyque © Gwladys Malalaka</p>
      </div>
      <div className="lg:col-span-7">
        <p className="text-xs uppercase tracking-[0.2em] text-coral">À propos</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">
          Je suis <span className="italic">Gwladys Malalaka</span> — artiste, thérapeute PSIQ, formatrice.
        </h2>
        <div className="mt-8 space-y-5 text-ink/80 text-base md:text-lg leading-relaxed">
          <p>Née d'un tissage entre La Réunion, Madagascar, la Bretagne et l'Indonésie, je porte un héritage complexe — entre lignées esclaves et colons — qui nourrit aujourd'hui ma pratique.</p>
          <p><strong className="text-deep-teal">PSIQ</strong> signifie <em>Psychanalyse Structurale Informationnelle et Quantique</em>. Cette méthode a été formalisée par Martine Selleret, psychanalyste depuis plus de trente ans.</p>
          <p>Huit années de formation PSIQ (2009–2017) — où j'ai travaillé en profondeur les grands thèmes psychanalytiques (Freud, Lacan, Carl Jung, Mélanie Klein, Wilhelm Reich). Un parcours d'artiste plasticienne (ENSAD Limoges, grade valant Master), mère de quatre enfants, aujourd'hui mamie. Des accompagnements ont été réalisés pendant ma formation et ponctuellement, sans gratification.</p>
          <p>Je suis <strong className="text-deep-teal">formatrice de la méthode Boclet</strong>. Et je me forme continuellement notamment par la <strong className="text-deep-teal">méthode Quick Change de Théo Duverger</strong> (prévention de la rechute burn-out, gestion du temps, focus et concentration). Mon développement personnel est permanent — pour mieux vous accompagner.</p>
          <p>Aujourd'hui, <strong className="text-deep-teal">je suis prête et confiante pour vous accompagner</strong>. C'est pourquoi je propose la première séance offerte : pour que vous puissiez constater par vous-même.</p>
          <p className="font-display italic text-deep-teal text-xl">« Malalaka » signifie <span className="text-coral">libre</span> en malgache.</p>
        </div>
      </div>
    </div>
  </section>
);

const MODULES = [
  "La Mère","Le Père","L'Œdipe","La Mort","La Frustration","Le Pouvoir","L'Orgasme","Le Cliché","La Culpabilité","L'Amour","Le Fœtal","La Naissance","L'Oral","L'Anal","Le Génital"
];

const THEMATIQUES = [
  "Votre corps vous appartient-il vraiment ?",
  "Oser dire non : non, c'est non.",
  "La reconquête de son corps — pendant et après la grossesse",
  "Le rôle des femmes et l'éducation des enfants",
  "« Et moi dans tout ça, est-ce que j'existe ? »",
  "Le poids des générations & secrets de famille",
  "Couvade — pour les hommes",
  "Syndrome du nid vide",
  "Intimité et sexualité du couple — avant, pendant et après bébé",
  "Difficulté à poser des limites",
  "Charge mentale, burn-out & prévention de la rechute",
  "Loyautés invisibles & transmission implicite des rôles féminins",
  "Libération des croyances limitantes & héritage spirituel",
  "Relations toxiques — reconnaître, s'extraire, se reconstruire",
  "Manque de confiance en soi & reconquête intérieure",
];

// Mots-clés SEO — visibles pour Google, discrets pour les visiteurs
const SEO_KEYWORDS = "thérapie relations sexuelles avant et après accouchement, difficulté à poser des limites, surcharge mentale et burn-out, loyauté forte, secrets de famille, envoûtement et croyances comment en sortir, transmission implicite des rôles féminins, couple avant pendant et après bébé, relation toxique, respect du corps des femmes pendant et après la grossesse, manque de confiance en soi, Limoges, visio, Haute-Vienne, psychanalyse holistique, mémoire cellulaire, héritage transgénérationnel";

const Methode = () => (
  <section id="methode" className="py-24 lg:py-32 bg-deep-teal text-ivory relative overflow-hidden protected-text" data-testid="section-methode" onContextMenu={(e) => e.preventDefault()}>
    <div className="absolute inset-0 opacity-15">
      <img src={ASSETS.sea} alt="" aria-hidden="true" className="w-full h-full object-cover" />
    </div>
    <div className="absolute inset-0 bg-deep-teal/85" aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "#E8A78D" }}>La méthode PSIQ</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl leading-tight">
          Une psychanalyse du <span className="italic">ressenti</span>, de la <span className="italic">cellule</span>, de la <span className="italic">mémoire</span>.
        </h2>
        <div className="mt-6 space-y-4 text-ivory/85 text-base md:text-lg leading-relaxed">
          <p>La PSIQ <strong>stimule les cellules</strong> en dehors de l'intellect et du mental. Cette approche nous touche au cœur du problème en utilisant le fonctionnement de la cellule.</p>
          <p>La cellule est composée d'eau. L'eau porte des informations déjà présentes dans le ventre de la mère — et bien avant elle. La PSIQ, dans le subtil, révèle les mémoires et redonne la vie.</p>
          <p>Ce n'est pas une promesse ou des miracles : nous sommes mort·es d'une certaine façon tant que nous vivons par procuration. La PSIQ enlève les masques, abat les cartes, déjoue les pièges de la psyché. Elle est là — <em>vivante, vibrante</em>, dans l'instant « T », dans une <strong>« vérité vraie »</strong>. Elle remet les points sur les « i » et les barres sur les « T ». Oui, c'est tout ça à la fois. Accessible, nous accédons enfin à notre <em>moi profond</em>. Elle touche l'inconscient, l'essentiel.</p>
          <p>Cette méthode d'analyse fait appel principalement à une <strong>logique intuitive</strong>, qui stimule le ressenti par le biais d'<em>inducteurs</em> dans chacun des modules.</p>
          <p className="font-display italic" style={{ color: "#E8A78D" }}>La cellule devient la vérité — non pas le thérapeute.</p>
          <p>D'ailleurs, <strong>les modules ont été co-créés par les personnes qui sont passées à la casserole bien avant moi</strong>. C'est en cela que cette méthode est vraie : basée sur du vécu à un moment « T », travaillé dans l'ici et maintenant. Nous ne pouvons que nous reconnaître et être touché·es. Surpris·e par tant de simplicité — et non simpliste — d'une connexion entre le corps et l'esprit.</p>
          <p className="italic">Ce n'est pas de l'ésotérisme — même s'il y a une part de magie lorsque la personne qui consulte décide de faire confiance en son thérapeute.</p>
          <p style={{ color: "#E8A78D" }}>La cellule n'a pas de race, de religion, de culture, d'orthographe — elle s'adresse à tous·tes celles et ceux qui ont l'envie profonde d'avancer. <strong>Le ressenti et le corps ne mentent pas.</strong></p>
        </div>
      </div>
      <h3 className="mt-14 font-display text-2xl text-ivory">Les 15 modules inducteurs</h3>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {MODULES.map((m, i) => (
          <div key={i} className="group relative p-5 rounded-2xl bg-ivory/5 border border-ivory/15 hover:bg-coral hover:border-coral transition-colors duration-300" data-testid={`module-${i+1}`}>
            <p className="font-display text-4xl group-hover:text-cream transition-colors" style={{ color: "#E8A78D" }}>{String(i+1).padStart(2,"0")}</p>
            <p className="mt-2 font-display text-lg text-ivory group-hover:text-cream">{m}</p>
          </div>
        ))}
      </div>
      <h3 className="mt-14 font-display text-2xl text-ivory">Autres thématiques abordées</h3>
      <div className="mt-5 flex flex-wrap gap-3">
        {THEMATIQUES.map((t, i) => (
          <span key={i} className="px-4 py-2 rounded-full bg-ivory/10 border border-ivory/20 text-sm text-ivory/90" data-testid={`thematique-${i}`}>{t}</span>
        ))}
      </div>
      {/* SEO — mots-clés naturels pour référencement Google (lecteurs d'écran uniquement) */}
      <p className="sr-only" data-testid="seo-keywords">{SEO_KEYWORDS}</p>
      <div className="mt-12 p-6 rounded-2xl bg-ivory/5 border border-ivory/15 flex items-start gap-4 max-w-3xl">
        <Lock size={20} className="text-coral shrink-0 mt-1" />
        <p className="text-sm text-ivory/85"><strong>Confidentialité absolue des séances.</strong> Ce qui se dit et se vit en séance reste strictement entre nous. Cadre éthique, respect du rythme de chacun·e.</p>
      </div>
    </div>
  </section>
);

const ACCOMPS = [
  { icon: Heart, title: "Individuel", desc: "Séance en présentiel à Limoges. Espace intime, confidentiel, à votre rythme.", time: "1h" },
  { icon: Users, title: "Groupe", desc: "Cercles de parole et exploration collective des modules PSIQ.", time: "1h à 2h" },
  { icon: Video, title: "Visio", desc: "Même profondeur, chez vous. Outre-mer, province, étranger : bienvenue.", time: "1h" },
];

const Accompagnements = () => (
  <section id="accompagnements" className="py-24 lg:py-32 bg-ivory" data-testid="section-accompagnements">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-coral">Accompagnements</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">Trois portes d'entrée.<br/> Une même <span className="italic">présence</span>.</h2>
        </div>
        <p className="max-w-md text-ink/70">J'accompagne principalement des femmes qui se sentent dépassées, ainsi que des hommes en couvade et toute personne en quête transgénérationnelle — celles et ceux qui sentent que le corps en sait plus que les mots.</p>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {ACCOMPS.map((a, i) => (
          <article key={i} className="group p-8 rounded-3xl bg-cream border border-ink-soft hover:border-deep-teal transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-testid={`accomp-${i}`}>
            <div className="w-12 h-12 rounded-2xl bg-deep-teal text-cream grid place-items-center">
              <a.icon size={22} />
            </div>
            <h3 className="mt-6 font-display text-2xl text-deep-teal">{a.title}</h3>
            <p className="mt-3 text-ink/75 leading-relaxed">{a.desc}</p>
            <p className="mt-5 text-sm text-coral font-medium">Durée : {a.time}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const TARIFS = [
  { title: "1ʳᵉ séance découverte", price: "Offerte", note: "En visio · En échange d'un avis Google", highlight: true },
  { title: "Individuel", price: "80€", note: "1h en présentiel ou visio" },
  { title: "Groupe · 1h", price: "60€", note: "par participant" },
  { title: "Groupe · 1h30", price: "80€", note: "par participant" },
  { title: "Groupe · 2h", price: "100€", note: "par participant" },
];

const Tarifs = () => {
  const [show, setShow] = useState(false);
  return (
    <section id="tarifs" className="py-24 lg:py-32 bg-cream" data-testid="section-tarifs">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-coral">Tarifs</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight max-w-3xl">Une première rencontre <span className="italic">offerte</span> en visio — ou à valider directement avec moi par téléphone.</h2>
        <p className="mt-6 max-w-2xl text-ink/70">La tarification est communiquée avec transparence lors du premier échange — ou ici si vous souhaitez la consulter dès maintenant.</p>
        <label className="mt-6 inline-flex items-center gap-3 cursor-pointer select-none text-sm text-deep-teal" data-testid="toggle-tarifs-label">
          <input type="checkbox" className="w-4 h-4 accent-coral" checked={show} onChange={() => setShow(!show)} data-testid="toggle-tarifs" />
          <span className="inline-flex items-center gap-2">{show ? <EyeOff size={16}/> : <Eye size={16}/>} {show ? "Masquer les tarifs" : "Afficher les tarifs"}</span>
        </label>
        {show && (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-5 rise" data-testid="tarifs-grid">
            {TARIFS.map((t, i) => (
              <div key={i} className={`p-7 rounded-3xl border ${t.highlight ? "bg-coral text-cream border-coral" : "bg-ivory border-ink-soft"}`} data-testid={`tarif-${i}`}>
                <p className={`text-sm ${t.highlight ? "text-cream/80" : "text-ink/60"}`}>{t.title}</p>
                <p className="mt-3 font-display text-4xl">{t.price}</p>
                <p className={`mt-2 text-sm ${t.highlight ? "text-cream/85" : "text-ink/70"}`}>{t.note}</p>
                {t.highlight && <Sparkles className="mt-4" size={18} />}
              </div>
            ))}
          </div>
        )}
        <p className="mt-10 text-sm text-ink/60 italic max-w-3xl">Pratique de thérapie holistique PSIQ, distincte de la médecine conventionnelle. Ne se substitue pas à un suivi médical, psychiatrique ou psychologique.</p>
      </div>
    </section>
  );
};

const QUIZ = [
  { q: "Ce qui vous pèse le plus aujourd'hui ?", opts: [
    { t: "Un héritage familial qui se répète", mod: "Le Cliché" },
    { t: "Un épuisement profond (burn-out)", mod: "La Frustration" },
    { t: "Une perte, un départ, un vide", mod: "La Mort" },
    { t: "Le sentiment de ne pas exister", mod: "Le Fœtal" },
  ]},
  { q: "Dans votre corps, vous ressentez…", opts: [
    { t: "Une tension permanente", mod: "L'Anal" },
    { t: "Une coupure, une absence", mod: "Le Génital" },
    { t: "Un manque, une faim émotionnelle", mod: "L'Oral" },
    { t: "Une colère contenue", mod: "Le Pouvoir" },
  ]},
  { q: "Ce que vous cherchez vraiment ?", opts: [
    { t: "Reprendre ma place", mod: "Le Père" },
    { t: "Me réconcilier avec la maternité", mod: "La Mère" },
    { t: "Aimer et être aimé·e autrement", mod: "L'Amour" },
    { t: "Me libérer d'une culpabilité", mod: "La Culpabilité" },
  ]},
];

const Quiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  const choose = (opt) => {
    const next = [...answers, opt];
    setAnswers(next);
    if (step < QUIZ.length - 1) setStep(step + 1);
    else {
      const counts = {};
      next.forEach(o => { counts[o.mod] = (counts[o.mod] || 0) + 1; });
      const top = Object.entries(counts).sort((a,b)=>b[1]-a[1])[0][0];
      setResult(top);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    try {
      // Enregistre email + module résultat + réponses détaillées
      await axios.post(`${API}/quiz`, {
        email,
        module: result,
        answers: answers.map(a => ({ question: a.t, module: a.mod })),
      });
      toast.success("Merci ! Vous recevrez bientôt des ressources.");
      setSubmitted(true);
    } catch { toast.error("Une erreur est survenue."); }
  };

  const reset = () => { setStep(0); setAnswers([]); setResult(null); setSubmitted(false); setEmail(""); };

  return (
    <section id="quiz" className="py-24 lg:py-32 bg-sage-soft/40" data-testid="section-quiz">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-coral">Quiz PSIQ</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">Quel module PSIQ vous <span className="italic">appelle</span> ?</h2>
        <p className="mt-4 text-ink/75">3 questions intuitives. Répondez en écoutant votre <em>corps</em>, vos <em>émotions</em> — pas avec la tête.</p>

        <div className="mt-10 p-8 md:p-10 rounded-3xl bg-cream border border-ink-soft" data-testid="quiz-card">
          {result === null ? (
            <div>
              <p className="text-sm text-ink/60">Question {step + 1} / {QUIZ.length}</p>
              <h3 className="mt-3 font-display text-2xl md:text-3xl text-deep-teal">{QUIZ[step].q}</h3>
              <div className="mt-6 space-y-3">
                {QUIZ[step].opts.map((o, i) => (
                  <button key={i} onClick={() => choose(o)} className="w-full text-left p-4 rounded-2xl border border-ink-soft hover:border-deep-teal hover:bg-ivory transition-colors" data-testid={`quiz-opt-${step}-${i}`}>
                    {o.t}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="quiz-result">
              <p className="text-sm text-coral uppercase tracking-widest">Votre module</p>
              <h3 className="mt-2 font-display text-4xl md:text-5xl text-deep-teal">{result}</h3>
              <p className="mt-4 text-ink/80">Ce module résonne particulièrement avec ce que vous traversez. Recevez par email une première piste de travail et une invitation à la séance découverte offerte.</p>
              {!submitted ? (
                <form onSubmit={submitEmail} className="mt-6 flex flex-col sm:flex-row gap-3">
                  <input required type="email" placeholder="votre.email@exemple.fr" value={email} onChange={e => setEmail(e.target.value)} className="flex-1 px-4 py-3 rounded-xl bg-ivory border border-ink-soft focus:border-deep-teal outline-none" data-testid="quiz-email" />
                  <button type="submit" className="btn-primary" data-testid="quiz-submit">Recevoir ma piste</button>
                </form>
              ) : (
                <p className="mt-6 text-sage font-display text-xl italic">Merci. Je vous écris très bientôt. 🌿</p>
              )}
              <button onClick={reset} className="mt-6 text-sm underline text-ink/60 hover:text-deep-teal" data-testid="quiz-reset">Refaire le quiz</button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const AFAKA_VOLETS = [
  { icon: Heart, title: "Physique" },
  { icon: Feather, title: "Psychique" },
  { icon: Waves, title: "Émotionnel" },
  { icon: Sparkles, title: "Énergétique" },
  { icon: Shield, title: "Spirituel" },
];

const AFAKA_OUTILS = [
  { n: "01", titre: "PSIQ", desc: "Psychanalyse Structurale Informationnelle et Quantique. 15 modules inducteurs. Méthode Selleret." },
  { n: "02", titre: "Méthode Boclet", desc: "Reprendre son cerveau en main : mémorisation, lecture rapide, mind-mapping, concentration, révision espacée, méthodes d'apprentissage." },
  { n: "03", titre: "Bio-thérapie", desc: "Nutrition, micronutrition, énergétique, symbolique corps-émotions — pour retrouver son équilibre vital." },
];

const Afaka = () => (
  <section id="afaka" className="py-24 lg:py-32 bg-cream relative overflow-hidden protected-text" data-testid="section-afaka" onContextMenu={(e) => e.preventDefault()}>
    <div className="absolute -top-20 left-0 w-[28rem] h-[28rem] rounded-full bg-sage-soft/40 blur-3xl" aria-hidden="true" />
    <div className="absolute bottom-0 right-0 w-[24rem] h-[24rem] rounded-full blur-3xl" style={{ background: "rgba(232,167,141,.25)" }} aria-hidden="true" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
      <div className="max-w-3xl">
        <p className="text-xs uppercase tracking-[0.2em] text-coral flex items-center gap-2"><Sparkles size={14} /> Ma démarche holistique</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">
          La méthode <span className="italic text-coral">AFAKA</span> — prendre soin de soi, <span className="italic">pleinement</span>.
        </h2>
        <p className="mt-6 text-ink/80 text-base md:text-lg leading-relaxed">
          <strong>AFAKA</strong> — signifie « libérer » et « réussir » en malgache, écho à <em>Malalaka</em> (libre). C'est ma démarche holistique, née de mon propre chemin de guérison, rechute et rétablissement. J'accompagne <strong>les femmes en post burn-out, dépassées dans leur quotidien</strong> — grossesse, accouchement, baby-blues, séparation, dépression, surcharge émotionnelle — à <strong>se reprendre en main</strong> sur plusieurs plans à la fois.
        </p>
        <p className="mt-4 text-ink/80 text-base md:text-lg leading-relaxed">
          Ces méthodes, je les ai moi-même expérimentées et intégrées. Constatant leur efficacité, je me suis formée pour aujourd'hui vous les transmettre — avec respect, unicité et ajustement à votre situation.
        </p>
      </div>

      {/* Les 5 volets (3 actuels + 2 bientôt) */}
      <div className="mt-14">
        <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Les 5 volets de l'accompagnement</p>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {AFAKA_VOLETS.map((v, i) => (
            <article key={i} className="p-6 rounded-2xl bg-ivory border border-ink-soft hover:border-deep-teal transition-colors" data-testid={`afaka-volet-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-deep-teal text-cream grid place-items-center">
                <v.icon size={18} />
              </div>
              <h3 className="mt-4 font-display text-lg text-deep-teal">{v.title}</h3>
              {i >= 3 && <p className="mt-3 text-xs uppercase tracking-widest text-coral">En développement</p>}
            </article>
          ))}
        </div>
      </div>

      {/* 3 outils principaux */}
      <div className="mt-14">
        <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Les 3 outils articulés</p>
        <div className="grid md:grid-cols-3 gap-6">
          {AFAKA_OUTILS.map((o, i) => (
            <div key={i} className="p-7 rounded-3xl bg-deep-teal text-cream relative overflow-hidden" data-testid={`afaka-outil-${i}`}>
              <p className="font-display text-5xl text-coral-soft" style={{color:"#E8A78D"}}>{o.n}</p>
              <h3 className="mt-3 font-display text-2xl">{o.titre}</h3>
              <p className="mt-3 text-sm text-ivory/85 leading-relaxed">{o.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Accompagnement concret post-burn-out */}
      <div className="mt-14 p-8 md:p-10 rounded-3xl bg-ivory border border-ink-soft" data-testid="afaka-accompagnement">
        <p className="text-xs uppercase tracking-[0.2em] text-coral">Pour qui ?</p>
        <h3 className="mt-3 font-display text-2xl md:text-3xl text-deep-teal leading-tight">Femmes en reconstruction — <span className="italic">après un burn-out</span>, une dépression, une grande traversée.</h3>
        <p className="mt-5 text-ink/80 leading-relaxed">
          Vous vous reconnaissez peut-être ici :
        </p>
        <ul className="mt-6 grid sm:grid-cols-2 gap-4 text-ink/85" data-testid="afaka-portes">
          {[
            { icon: Waves,     title: "Submergée au quotidien",       desc: "plus rien ne tient, tout vous échappe" },
            { icon: Shield,    title: "Prise dans une relation qui vous éteint", desc: "couple, famille, travail — vous ne vous reconnaissez plus" },
            { icon: Feather,   title: "Vous doutez de vous",         desc: "vous culpabilisez pour tout, l'estime s'effrite" },
            { icon: Sparkles,  title: "Vous voulez retrouver votre élan", desc: "sans savoir par où commencer" },
          ].map(({ icon: Ico, title, desc }, i) => (
            <li key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-white/60 border border-ink-soft/60">
              <Ico size={20} className="text-coral mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-deep-teal text-sm md:text-base">{title}</p>
                <p className="text-sm text-ink/70 leading-relaxed">{desc}</p>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-8 italic text-deep-teal font-display text-lg leading-snug">
          Chaque accompagnement est <strong className="not-italic text-coral">unique</strong>. Ce qui se passe entre nous vous appartient.
        </p>
        <p className="mt-4 text-ink/80 leading-relaxed">
          <strong className="text-deep-teal">Racontez-moi ce que vous vivez</strong> — en toute confidentialité. Nous verrons ensemble, sans engagement, si mon accompagnement vous correspond.
        </p>
        <p className="mt-6 italic text-deep-teal/80 font-display">Nous sommes <strong className="text-coral not-italic">responsables</strong>, mais pas coupables.</p>
        <div className="mt-8">
          <button onClick={() => scrollTo("contact")} className="btn-coral inline-flex items-center gap-2" data-testid="afaka-cta">
            Me parler de ma situation <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  </section>
);

const Formations = () => (
  <section id="formations" className="py-24 lg:py-32 bg-cream protected-text" data-testid="section-formations" onContextMenu={(e) => e.preventDefault()}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-coral">Formations &amp; transmission</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">Art, psychanalyse <span className="italic">&amp;</span> réappropriation du <span className="italic">cerveau</span>.</h2>
        <p className="mt-6 text-ink/75 text-lg">Pour enseignants, artistes, professionnels de l'accompagnement, institutions culturelles, ou toute personne souhaitant reprendre son cerveau et sa vie en main.</p>

        <div className="mt-10 space-y-6">
          <div data-testid="formation-block-boclet">
            <p className="text-xs uppercase tracking-widest text-coral">Méthode Boclet — réapprendre à apprendre</p>
            <p className="mt-2 text-sm text-ink/80 leading-relaxed">
              Reprendre son cerveau en main : retrouver concentration, mémoire et confiance dans ses capacités d'apprentissage. Le programme détaillé est transmis aux participantes.
            </p>
          </div>

          <div data-testid="formation-block-biotherapie">
            <p className="text-xs uppercase tracking-widest text-coral">Bio-thérapie — nutrition &amp; énergétique</p>
            <p className="mt-2 text-sm text-ink/80 leading-relaxed">
              Comprendre ce que le corps exprime — nutrition, énergétique et symbolique corps-émotions — pour retrouver son équilibre vital. Le programme détaillé est transmis aux participantes.
            </p>
            <p className="mt-2 text-xs text-ink/60 italic">Enseignements reçus du Dr Michel Vielpeau.</p>
          </div>

          <div data-testid="formation-block-art">
            <p className="text-xs uppercase tracking-widest text-coral">Art &amp; psychanalyse</p>
            <p className="mt-2 text-sm text-ink/80 leading-relaxed">L'image, le geste, la matière comme voies d'accès à l'inconscient. Ateliers mensuels (Limoges &amp; visio), conférences sur demande, interventions en milieu institutionnel.</p>
          </div>
        </div>

        <button onClick={() => scrollTo("contact")} className="mt-10 btn-coral inline-flex items-center gap-2" data-testid="formations-cta">
          Demander le programme <ArrowRight size={18} />
        </button>
      </div>

      <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden">
        <img src={ASSETS.sea} alt="Sous l'eau — illustration de profondeur intérieure" className="w-full h-full object-cover protected-img" onContextMenu={(e) => e.preventDefault()} onDragStart={(e) => e.preventDefault()} />
        <div className="absolute bottom-6 left-6 right-6 bg-cream/90 backdrop-blur-sm rounded-2xl p-5">
          <p className="font-display italic text-deep-teal">Un voyage dans nos eaux cellulaires.</p>
        </div>
      </div>
    </div>
  </section>
);

const Galerie = () => {
  const items = [
    { src: ASSETS.triptyque, t: "Autobiographie — triptyque", y: "Œuvre personnelle" },
    { src: ASSETS.enfantLumiere, t: "Un enfant dans la lumière sortant de l'ombre", y: "Œuvre personnelle", fallback: ASSETS.triptyque },
  ];
  return (
    <section id="galerie" className="py-24 lg:py-32 bg-ivory" data-testid="section-galerie">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-coral">Galerie protégée</p>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">Œuvres — <span className="italic">aperçus</span>.</h2>
          </div>
          <p className="max-w-sm text-ink/70 text-sm"><Shield size={14} className="inline mr-1 text-teal"/> Œuvres personnelles protégées par filigrane. Reproduction interdite sans autorisation écrite.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <figure key={i} className="group" data-testid={`galerie-item-${i}`}>
              <div className="wm relative aspect-[4/5] rounded-2xl overflow-hidden bg-ivory-2">
                <img src={it.src} alt={`${it.t} par Gwladys Malalaka`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 protected-img" onContextMenu={(e)=>e.preventDefault()} onDragStart={(e) => e.preventDefault()} onError={(e)=>{ if (it.fallback && e.target.src !== it.fallback) e.target.src = it.fallback; }} />
              </div>
              <figcaption className="mt-3 flex justify-between text-sm">
                <span className="font-display italic text-deep-teal">{it.t}</span>
                <span className="text-ink/50">{it.y}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const CALENDLY_URL = "https://calendly.com/gwladysmalalaka";

const Rdv = () => {
  useEffect(() => {
    // Charge le script Calendly une seule fois
    const id = "calendly-script";
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <section id="rdv" className="py-24 lg:py-32 bg-ivory-2" data-testid="section-rdv">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-coral flex items-center gap-2"><Calendar size={14}/> Réservation en ligne</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl text-deep-teal leading-tight">Prendre rendez-vous en <span className="italic">visio</span> ou au cabinet.</h2>
        <p className="mt-4 text-ink/75 max-w-2xl">
          Choisissez votre créneau directement dans le calendrier ci-dessous. Vous recevrez automatiquement un email de confirmation et un rappel <strong>24h</strong> puis <strong>1h</strong> avant votre séance. La 1ʳᵉ séance découverte est <strong className="text-coral">offerte</strong> en échange d'un avis Google honnête.
        </p>
        <p className="mt-3 text-sm text-ink/60">Préférez le contact direct ? <a href="mailto:psiq@gwladysmalalaka.com" className="text-deep-teal underline">psiq@gwladysmalalaka.com</a> · <a href="tel:0782850043" className="text-deep-teal underline">07 82 85 00 43</a></p>

        <div
          className="calendly-inline-widget mt-10 rounded-3xl overflow-hidden border border-ink-soft bg-cream"
          data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=d46a4a`}
          style={{ minWidth: "320px", height: "780px" }}
          data-testid="calendly-widget"
        />

        <div className="mt-6 p-5 rounded-2xl bg-cream border border-ink-soft flex flex-col sm:flex-row sm:items-center gap-4" data-testid="calendly-fallback">
          <p className="text-sm text-ink/70 flex-1">Le calendrier ne s'affiche pas ? Certains navigateurs (comme Firefox) le bloquent par sécurité. Pas d'inquiétude :</p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-coral text-ivory font-medium hover:opacity-90 transition-opacity shrink-0"
            data-testid="calendly-open-new-tab"
          >
            Ouvrir le calendrier <ArrowRight size={16} />
          </a>
        </div>

        <p className="mt-4 text-xs text-ink/50">
          Calendrier propulsé par Calendly. Vos données restent confidentielles et ne sont utilisées que pour gérer le rendez-vous.
        </p>
      </div>
    </section>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", sujet: "individuel", message: "", consentement: true });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé. Je vous réponds sous 48h.");
      setForm({ nom: "", email: "", telephone: "", sujet: "individuel", message: "", consentement: true });
    } catch { toast.error("Erreur d'envoi. Appelez-moi au 07 82 85 00 43."); }
    finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-deep-teal text-ivory relative overflow-hidden" data-testid="section-contact">
      <div className="absolute -top-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-coral/20 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "#E8A78D" }}>Prendre rendez-vous</p>
          <h2 className="mt-3 font-display text-3xl md:text-5xl leading-tight">Le premier pas — <span className="italic">offert</span>.</h2>
          <p className="mt-6 text-ivory/80">Racontez-moi en quelques lignes ce qui vous amène. Confidentialité absolue. Je vous propose une première rencontre gratuite pour sentir si notre travail ensemble est juste.</p>
          <div className="mt-10 space-y-5 text-ivory/90">
            <a href="tel:0782850043" className="flex items-center gap-3 hover:text-coral-soft transition-colors" data-testid="contact-phone"><Phone size={18} /> 07 82 85 00 43</a>
            <a href="mailto:psiq@gwladysmalalaka.com" className="flex items-center gap-3 hover:text-coral-soft transition-colors" data-testid="contact-email"><Mail size={18} /> psiq@gwladysmalalaka.com</a>
            <p className="flex items-start gap-3"><MapPin size={18} className="mt-1 shrink-0" /> <span>Bâtiment 25 — Limoges, Haute-Vienne<br/><span className="text-ivory/60 text-sm">&amp; en visio partout en France et à l'international</span></span></p>
            <div className="mt-4 rounded-2xl overflow-hidden border border-ivory/15" data-testid="contact-map">
              <iframe
                title="Carte — Cabinet Limoges"
                src="https://www.google.com/maps?q=Limoges,France&output=embed"
                width="100%" height="220" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <p className="flex items-start gap-3 text-sm text-ivory/70"><Lock size={16} className="mt-1 shrink-0" /> Échanges confidentiels — RGPD respecté.</p>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 bg-ivory text-ink rounded-[2rem] p-8 md:p-10 space-y-5" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink/60">Nom</span>
              <input required value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-xl bg-cream border border-ink-soft focus:border-deep-teal outline-none transition-colors" data-testid="input-nom" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink/60">Email</span>
              <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-xl bg-cream border border-ink-soft focus:border-deep-teal outline-none transition-colors" data-testid="input-email" />
            </label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink/60">Téléphone</span>
              <input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-xl bg-cream border border-ink-soft focus:border-deep-teal outline-none transition-colors" data-testid="input-telephone" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink/60">Sujet</span>
              <select value={form.sujet} onChange={e => setForm({...form, sujet: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-xl bg-cream border border-ink-soft focus:border-deep-teal outline-none transition-colors" data-testid="select-sujet">
                <option value="individuel">Séance individuelle</option>
                <option value="groupe">Séance de groupe</option>
                <option value="visio">Visio</option>
                <option value="formation">Formation / atelier</option>
                <option value="autre">Autre</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink/60">Votre message</span>
            <textarea required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="mt-1 w-full px-4 py-3 rounded-xl bg-cream border border-ink-soft focus:border-deep-teal outline-none transition-colors resize-none" data-testid="input-message" />
          </label>
          <label className="flex items-start gap-3 text-sm text-ink/70">
            <input type="checkbox" checked={form.consentement} onChange={e => setForm({...form, consentement: e.target.checked})} className="mt-1" data-testid="input-consentement" />
            <span>J'accepte que mes informations soient utilisées uniquement pour me recontacter (RGPD).</span>
          </label>
          <button disabled={loading} type="submit" className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="submit-contact">
            {loading ? "Envoi…" : (<>Envoyer mon message <ArrowRight size={18} /></>)}
          </button>
        </form>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-ink text-ivory/80 py-14" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-10">
      <div>
        <p className="font-display text-2xl text-cream">Gwladys <span className="italic text-coral">Malalaka</span></p>
        <p className="mt-3 text-sm max-w-sm">Psychanalyse PSIQ · Art &amp; Transmission. Pratique holistique, distincte de la médecine conventionnelle.</p>
      </div>
      <div className="text-sm space-y-2">
        <p className="uppercase tracking-widest text-xs text-cream/60 mb-3">Cabinet Gwladys Malalaka</p>
        <p>Bâtiment 25 — Limoges, Haute-Vienne</p>
        <p>07 82 85 00 43</p>
        <p>psiq@gwladysmalalaka.com</p>
      </div>
      <div className="text-sm space-y-2">
        <p className="uppercase tracking-widest text-xs text-cream/60 mb-3">Mentions</p>
        <p>Micro-entreprise · <em>SIRET en cours d'immatriculation</em></p>
        <p>Confidentialité des séances garantie. Ne se substitue pas à un suivi médical.</p>
        <div className="mt-3">
          <a href="/mentions-legales" className="underline text-cream/80 hover:text-coral transition-colors" data-testid="footer-mentions-legales">Mentions légales &amp; Politique de confidentialité</a>
        </div>
        <div className="flex gap-4 mt-4">
          <a href="#" aria-label="Instagram" className="hover:text-coral transition-colors" data-testid="social-instagram"><Instagram size={18} /></a>
          <a href="#" aria-label="LinkedIn" className="hover:text-coral transition-colors" data-testid="social-linkedin"><Linkedin size={18} /></a>
        </div>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-ivory/15 flex flex-wrap justify-between gap-3 text-xs text-cream/50">
      <p>© {new Date().getFullYear()} Gwladys Malalaka. Tous droits réservés.</p>
      <p>gwladysmalalaka.com · .fr</p>
    </div>
  </footer>
);

const Home = () => {
  useEffect(() => { document.title = "Gwladys Malalaka · Psychanalyste PSIQ · Thérapie holistique Limoges & visio"; }, []);
  return (
    <main className="bg-cream text-ink">
      <Nav />
      <Hero />
      <Marquee />
      <CeQuePSIQ />
      <Matrix />
      <APropos />
      <Methode />
      <Afaka />
      <Accompagnements />
      <Tarifs />
      <Quiz />
      <Rdv />
      <Formations />
      <Galerie />
      <Contact />
      <Footer />
    </main>
  );
};

export default Home;
